import type { Client } from '@thom/folio-domain/core/domain/client'
import { clientId as toClientId } from '@thom/folio-domain/core/domain/client'
import type { ClientFilter, ClientsPort } from '@thom/folio-domain/core/ports/clients'
import { err, ok, type Result } from '@thom/folio-domain/lib/result'
import { tryCatch } from '@thom/folio-domain/lib/try-catch'
import { Collections, type JournClientsResponse } from '@thom/folio-ui/pocketbase-types'
import { getPocketBaseClient } from './client'
import { keyed } from './request-key'
import { filterFor } from '@thom/pb/filter-builder'
import { paginate } from './paginate'

type ClientColumns = {
	id: string
	slug: string
	name: string
	created: Date
	updated: Date
}

const columns = (filter: ClientFilter) =>
	filterFor<ClientColumns>()([
		{ field: 'id', comparator: 'eq', value: filter.id },
		{ field: 'name', comparator: 'contains', value: filter.search },
	])

const toClient = (record: JournClientsResponse): Client => ({
	id: toClientId(record.id),
	slug: record.slug,
	name: record.name,
	site: record.site ?? '',
	logo: record.logo ?? '',
	descr: record.descr ?? '',
	createdAt: new Date(record.created),
	updatedAt: new Date(record.updated),
})

const createClientsAdapter = (): ClientsPort => {
	const client = getPocketBaseClient()
	const clients = () => client.collection(Collections.JournClients)

	return {
		list: async (filter: ClientFilter = {}): Promise<Result<readonly Client[]>> => {
			const { expr, params } = columns(filter)

			const { data, error } = await tryCatch(
				paginate<JournClientsResponse>(
					clients(),
					filter,
					keyed('clients.list', {
						filter: client.filter(expr, params),
						sort: 'name',
					}),
				),
			)

			return error
				? err(new Error(`Failed to list clients: ${error.message}`, { cause: error }))
				: ok(data.map(toClient))
		},

		get: async (ref: string): Promise<Result<Client>> => {
			const { expr, params } = filterFor<ClientColumns>()([{ field: 'slug', comparator: 'eq', value: ref }])

			const { data, error } = await tryCatch(
				clients().getFirstListItem<JournClientsResponse>(client.filter(expr, params), keyed('clients.get', {})),
			)

			return error
				? err(new Error(`Failed to load client ${ref}: ${error.message}`, { cause: error }))
				: ok(toClient(data))
		},
	}
}

export { columns, createClientsAdapter, toClient }
